import { devLog } from '../utils/devLog';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { backendApiUrl } from '../config/backendApiUrl';

const BackendAuthContext = createContext(null);

export const useBackendAuth = () => useContext(BackendAuthContext);

export const BackendAuthProvider = ({ children }) => {
  const [backendToken, setBackendToken] = useState(null);
  const [backendUser, setBackendUser] = useState(null);
  const [isBackendAuthenticated, setIsBackendAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); // Start with loading true for initial check
  const [error, setError] = useState(null);
  const [backendAuthReady, setBackendAuthReady] = useState(false);

  // Effect to check for existing token on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('backend_auth_token');
      devLog('[BackendAuthContext] Initializing');

      if (token) {
        setBackendToken(token);
        // Optional but recommended: Verify token with backend and get user data
        try {
          const response = await fetch(`${backendApiUrl('/api')}/backend-auth/verify`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.ok) {
            const user = await response.json();
            setBackendUser(user);
            setIsBackendAuthenticated(true);
            devLog('[BackendAuthContext] Token verified');
          } else {
            console.warn('[BackendAuthContext] Token verification failed, logging out.');
            logout();
          }
        } catch (err) {
          console.error('[BackendAuthContext] Failed to verify token:', err?.message || 'request failed');
          // Can't reach server, logout locally to be safe
          logout();
        }
      }
      setLoading(false);
      setBackendAuthReady(true);
    };

    initializeAuth();
  }, []); // Empty dependency array ensures this runs only once on mount

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);
    devLog('[BackendAuthContext] Attempting login...');

    try {
      const response = await fetch(`${backendApiUrl('/api')}/backend-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      devLog('[BackendAuthContext] Login successful');
      localStorage.setItem('backend_auth_token', data.token);
      setBackendToken(data.token);
      setBackendUser(data.user);
      setIsBackendAuthenticated(true);
      return data;
    } catch (err) {
      console.error('[BackendAuthContext] Login error:', err?.message || 'request failed');
      setError(err.message);
      setIsBackendAuthenticated(false);
      setBackendToken(null);
      setBackendUser(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    devLog('[BackendAuthContext] Logging out...');
    localStorage.removeItem('backend_auth_token');
    setBackendToken(null);
    setBackendUser(null);
    setIsBackendAuthenticated(false);
    // Optional: Notify backend of logout
    // fetch(`${API_BASE_URL}/backend-auth/logout`, { method: 'POST', headers: { 'Authorization': `Bearer ${backendToken}` } });
  }, []);

  const contextValue = {
    backendToken,
    backendUser,
    isBackendAuthenticated,
    loading,
    error,
    login,
    logout,
    backendAuthReady, // This flag tells consumers when the initial check is done
  };

  return <BackendAuthContext.Provider value={contextValue}>{children}</BackendAuthContext.Provider>;
};

export default BackendAuthContext;
